import * as React from "react";

import { RequestProductsSearch_Options } from "../../model";

import {
  decodeFacetNameFromExtraOptionsDataKey,
  EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY,
  EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY,
  EXTRA_OPTIONS_DATA_QUERY_KEY,
  PlpSelectedFacetOptionQueryExtraData,
  PlpState_UrlDataReadyToCache,
  PlpState_UrlDataTarget,
} from "../../url-state";

interface PlpDataTargetUrlStateConsumerInterface {
  transform: (state: PlpState_UrlDataTarget) => PlpState_UrlDataReadyToCache;
  extractOptionsPortionOfUrlDataWhichIsReadyToCache: (
    urlData: PlpState_UrlDataReadyToCache
  ) => RequestProductsSearch_Options;
}

const defaultValue = {} as unknown as PlpDataTargetUrlStateConsumerInterface;

const PlpDataTargetUrlStateContext = React.createContext(defaultValue);

export const PlpDataTargetUrlStateProvider: React.FunctionComponent = (
  props
) => {
  const encodeFacetNameToExtraOptionsDataKey = (key: string) => {
    return `${EXTRA_OPTIONS_DATA_QUERY_KEY}${key}`;
  };

  const encodeFacetNameToExtraOptionsConfigurationDataKey = (
    facetName: string
  ) => {
    return `${EXTRA_OPTIONS_CONFIGURATION_DATA_QUERY_KEY}${facetName}`;
  };

  const transformOptions = React.useCallback(
    (state: PlpState_UrlDataTarget) => {
      return state.selectedOptions.reduce(
        (
          aggregate: {
            [key: string]: string | string[];
          },
          selectedOption
        ) => {
          if (selectedOption.facetName) {
            const expectedAggregateKey = encodeFacetNameToExtraOptionsDataKey(
              selectedOption.facetName
            );
            if (selectedOption.facetIsMultiSelect) {
              let preexistingOptionAggregate =
                aggregate[expectedAggregateKey] || [];

              // It's possible that a value was populated from a partial query string,
              // where it's not immediately known whether it's an array or not,
              // and then a user option action occurs which knows it's a multi select,
              // so we need to make sure to correct the type as we go along
              if (!Array.isArray(preexistingOptionAggregate)) {
                preexistingOptionAggregate = [preexistingOptionAggregate];
              }

              preexistingOptionAggregate = [
                ...preexistingOptionAggregate,
                ...[selectedOption.id],
              ];
              aggregate = {
                ...aggregate,
                ...{
                  [expectedAggregateKey]: preexistingOptionAggregate,
                },
              };
            } else {
              aggregate = {
                ...aggregate,
                ...{
                  [expectedAggregateKey]: selectedOption.id,
                },
              };
            }
          }
          return aggregate;
        },
        {}
      );
    },
    []
  );

  const transformOptionsConfiguration = React.useCallback(
    (state: PlpState_UrlDataTarget) => {
      return state.selectedOptions.reduce(
        (
          aggregate: { [key: string]: [boolean, ...string[]] },
          selectedOption
        ) => {
          // only add extra data for fully formed options
          if (
            selectedOption.facetName &&
            selectedOption.name &&
            selectedOption.facetIsMultiSelect !== null &&
            selectedOption.facetIsMultiSelect !== undefined
          ) {
            const extraDataKey =
              encodeFacetNameToExtraOptionsConfigurationDataKey(
                selectedOption.facetName
              );
            if (selectedOption.facetIsMultiSelect) {
              let preexistingOptionAggregate: PlpSelectedFacetOptionQueryExtraData =
                aggregate[extraDataKey] || [];

              let [isMultiSelect, ...preexistingNameValues] =
                preexistingOptionAggregate;

              preexistingNameValues = preexistingNameValues || [];

              aggregate = {
                ...aggregate,
                ...{
                  [extraDataKey]: [
                    selectedOption.facetIsMultiSelect,
                    ...[...preexistingNameValues, ...[selectedOption.name]],
                  ],
                },
              };
            } else {
              aggregate = {
                ...aggregate,
                ...{
                  [extraDataKey]: [
                    selectedOption.facetIsMultiSelect,
                    selectedOption.name,
                  ],
                },
              };
            }
          }

          return aggregate;
        },
        {}
      );
    },
    []
  );

  const transformSort = React.useCallback((state: PlpState_UrlDataTarget) => {
    if (state.selectedSortingOption.value) {
      return {
        sort: state.selectedSortingOption.value,
      };
    }
    return {};
  }, []);

  const transformFacets = React.useCallback((state: PlpState_UrlDataTarget) => {
    let flatFacets: string[] = [];
    for (const [key, value] of Object.entries(state.facetCache)) {
      const thisFacet = [key, ...value];
      flatFacets = [...flatFacets, ...thisFacet];
    }
    const facetsData = {
      [EXTRA_FACET_DISPLAY_NAMES_QUERY_KEY]: flatFacets,
    };
    return facetsData;
  }, []);

  const transformPage = React.useCallback((state: PlpState_UrlDataTarget) => {
    const extraParametersForUrlBar = {
      page: state.page,
    };
    return extraParametersForUrlBar;
  }, []);

  const transform = React.useCallback(
    (state: PlpState_UrlDataTarget) => {
      let state_urlCacheStringifyReady;

      const options = transformOptions(state);
      state_urlCacheStringifyReady = {
        ...(state_urlCacheStringifyReady || {}),
        ...options,
      };

      const optionsConfiguration = transformOptionsConfiguration(state);
      state_urlCacheStringifyReady = {
        ...state_urlCacheStringifyReady,
        ...optionsConfiguration,
      };

      const sort = transformSort(state);
      state_urlCacheStringifyReady = {
        ...state_urlCacheStringifyReady,
        ...sort,
      };

      const facets = transformFacets(state);
      state_urlCacheStringifyReady = {
        ...state_urlCacheStringifyReady,
        ...facets,
      };

      const page = transformPage(state);
      state_urlCacheStringifyReady = {
        ...state_urlCacheStringifyReady,
        ...page,
      };

      return state_urlCacheStringifyReady;
    },
    [
      transformOptions,
      transformOptionsConfiguration,
      transformSort,
      transformFacets,
      transformPage,
    ]
  );

  const extractOptionsPortionOfUrlDataWhichIsReadyToCache = React.useCallback(
    (urlData: PlpState_UrlDataReadyToCache) => {
      const options = Object.entries(urlData).reduce(
        (
          aggregate: {
            [key: string]: string | string[];
          },
          [key, value]
        ) => {
          if (key.startsWith(EXTRA_OPTIONS_DATA_QUERY_KEY)) {
            const facetName = decodeFacetNameFromExtraOptionsDataKey(key);
            aggregate = {
              ...aggregate,
              ...{
                [facetName]: value as string | string[],
              },
            };
          }
          return aggregate;
        },
        {}
      );
      return options;
    },
    []
  );

  const value: PlpDataTargetUrlStateConsumerInterface = React.useMemo(
    () => ({
      transform,
      extractOptionsPortionOfUrlDataWhichIsReadyToCache,
    }),
    [transform, extractOptionsPortionOfUrlDataWhichIsReadyToCache]
  );
  return (
    <PlpDataTargetUrlStateContext.Provider value={value}>
      {props?.children || []}
    </PlpDataTargetUrlStateContext.Provider>
  );
};

export const usePlpDataTargetUrlState = () => {
  const context = React.useContext(PlpDataTargetUrlStateContext);
  if (context === defaultValue) {
    throw new Error(
      "usePlpDataTargetUrlState must be used within a PlpDataTargetUrlStateProvider"
    );
  }
  return context;
};
